import React from "react";
var __jsx = React.createElement;
import classNames from "classnames";
import copy from "copy-to-clipboard";
import { Fragment, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { trackEvent } from "../../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../../utils/mixpanelEvents";
import ReportAITags from "./ReportAITags";
import ReportStrip from "./ReportStrip";
import ReportVersionPanel from "./ReportVersionPanel";
var topRelativeTo = function topRelativeTo(el, container) {
  return el.getBoundingClientRect().top - container.getBoundingClientRect().top;
};

/**
 * An expandable card that renders a radiology report inside a chat message (or a modal).
 *
 * Collapsed, it shows a summary strip; expanding reveals version tabs, the selected
 * report version, and its AI tags. When a `scrollContainerRef` is supplied (the
 * flex-col-reverse chat list), the header is pinned in place during the expand
 * animation and the card is then scrolled just enough to sit fully within view,
 * clearing the top/bottom insets. Without one, it falls back to native scroll-into-view.
 */
var RadiologyReportCard = function RadiologyReportCard(_ref) {
  var _data$versions$0$id, _data$versions$, _data$versions$find, _activeVersion$aiTags, _activeVersion$aiTags2;
  var data = _ref.data,
    className = _ref.className,
    scrollContainerRef = _ref.scrollContainerRef,
    _ref$scrollTopInset = _ref.scrollTopInset,
    scrollTopInset = _ref$scrollTopInset === void 0 ? 12 : _ref$scrollTopInset,
    _ref$scrollBottomInse = _ref.scrollBottomInset,
    scrollBottomInset = _ref$scrollBottomInse === void 0 ? 16 : _ref$scrollBottomInse,
    _ref$showActions = _ref.showActions,
    showActions = _ref$showActions === void 0 ? false : _ref$showActions,
    onApplyTag = _ref.onApplyTag,
    applyingTagId = _ref.applyingTagId,
    studies = _ref.studies,
    onLinkStudy = _ref.onLinkStudy,
    hideDicom = _ref.hideDicom;
  var _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  var _useState2 = useState((_data$versions$0$id = (_data$versions$ = data.versions[0]) === null || _data$versions$ === void 0 ? void 0 : _data$versions$.id) !== null && _data$versions$0$id !== void 0 ? _data$versions$0$id : ""),
    activeVersionId = _useState2[0],
    setActiveVersionId = _useState2[1];
  var cardRef = useRef(null);
  var printRef = useRef(null);
  var activeVersion = (_data$versions$find = data.versions.find(function (v) {
    return v.id === activeVersionId;
  })) !== null && _data$versions$find !== void 0 ? _data$versions$find : data.versions[0];
  var reactToPrint = useReactToPrint({
    contentRef: printRef,
    preserveAfterPrint: true
  });
  var printFn = function printFn() {
    trackEvent(MIXPANEL_EVENTS.RADIOLOGY_REPORT_PRINTED, {
      label: data.examDescription,
      value: data.studyId
    });
    reactToPrint();
  };
  var onCopy = function onCopy() {
    if (!activeVersion) return;
    var plain = activeVersion.reportHtml.replace(/<[^>]+>/g, "");
    copy("".concat(data.examDescription, "\n\n").concat(plain), {
      format: "text/plain"
    });
    trackEvent(MIXPANEL_EVENTS.RADIOLOGY_REPORT_COPIED, {
      label: data.examDescription,
      value: activeVersion.id
    });
  };
  var onToggle = function onToggle() {
    var willExpand = !isExpanded;
    if (willExpand) {
      trackEvent(MIXPANEL_EVENTS.RADIOLOGY_REPORT_EXPANDED, {
        label: data.examDescription,
        value: data.studyId
      });
    }
    var container = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current;
    var el = cardRef.current;
    var pinnedTop = container && el ? topRelativeTo(el, container) : null;
    setIsExpanded(function (prev) {
      var _data$versions$0$id2, _data$versions$2;
      if (!prev) setActiveVersionId((_data$versions$0$id2 = (_data$versions$2 = data.versions[0]) === null || _data$versions$2 === void 0 ? void 0 : _data$versions$2.id) !== null && _data$versions$0$id2 !== void 0 ? _data$versions$0$id2 : "");
      return !prev;
    });
    var ensureFullyVisible = function ensureFullyVisible() {
      var c = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current;
      var e = cardRef.current;
      if (!c || !e) return;
      var cRect = c.getBoundingClientRect();
      var top = cRect.top + scrollTopInset;
      var bottom = cRect.bottom - scrollBottomInset;
      var eRect = e.getBoundingClientRect();
      var delta = 0;
      if (eRect.bottom > bottom) {
        // Reveal the hidden bottom, but don't pull the top above the visible region.
        delta = Math.min(eRect.bottom - bottom, Math.max(0, eRect.top - top));
      } else if (eRect.top < top) {
        delta = eRect.top - top;
      }
      if (Math.abs(delta) > 1) c.scrollBy({
        top: delta,
        behavior: "smooth"
      });
    };

    // No scroll container (modal / non-reverse lists): native minimal scroll-into-view.
    if (pinnedTop == null || !container || !el) {
      if (willExpand) {
        setTimeout(function () {
          var _cardRef$current;
          return (_cardRef$current = cardRef.current) === null || _cardRef$current === void 0 ? void 0 : _cardRef$current.scrollIntoView({
            block: "nearest",
            behavior: "smooth"
          });
        }, 240);
      }
      return;
    }
    var start = performance.now();
    var _keepPinned = function keepPinned() {
      var c = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current;
      var e = cardRef.current;
      if (!c || !e) return;
      var drift = topRelativeTo(e, c) - pinnedTop;
      if (Math.abs(drift) > 0.5) c.scrollBy({
        top: drift
      });
      if (performance.now() - start < 260) requestAnimationFrame(_keepPinned);else if (willExpand) ensureFullyVisible();
    };
    requestAnimationFrame(_keepPinned);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: cardRef,
    className: classNames("bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm w-full text-left", className)
  }, /*#__PURE__*/React.createElement(ReportStrip, {
    data: data,
    isExpanded: isExpanded,
    onToggle: onToggle,
    studies: studies,
    onLinkStudy: onLinkStudy,
    hideDicom: hideDicom
  }), !isExpanded && /*#__PURE__*/React.createElement(ReportAITags, {
    tags: (_activeVersion$aiTags = activeVersion === null || activeVersion === void 0 ? void 0 : activeVersion.aiTags) !== null && _activeVersion$aiTags !== void 0 ? _activeVersion$aiTags : [],
    title: "AI Tags",
    onApplyTag: onApplyTag,
    applyingTagId: applyingTagId,
    className: "rounded-b-xl"
  }), /*#__PURE__*/React.createElement("div", {
    className: classNames("grid grid-cols-1 transition-[grid-template-rows] duration-200 ease-in-out overflow-hidden rounded-b-xl", isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]")
  }, /*#__PURE__*/React.createElement("div", {
    className: "min-h-0 min-w-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-200 dark:border-gray-700"
  }, data.versions.length > 1 && /*#__PURE__*/React.createElement("div", {
    className: "flex px-[14px] bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 scrollbar-thin-x"
  }, data.versions.map(function (v, i) {
    return /*#__PURE__*/React.createElement(Fragment, {
      key: v.id
    }, i > 0 && /*#__PURE__*/React.createElement("span", {
      className: "self-center h-3.5 w-px shrink-0 bg-gray-200 dark:bg-gray-700"
    }), /*#__PURE__*/React.createElement("button", {
      onClick: function onClick() {
        if (v.id !== activeVersionId) {
          trackEvent(MIXPANEL_EVENTS.RADIOLOGY_REPORT_VERSION_SWITCHED, {
            label: v.label,
            value: data.studyId
          });
        }
        setActiveVersionId(v.id);
      },
      className: classNames("flex shrink-0 items-center gap-1.5 px-2.5 pt-[7px] pb-[6px] text-[11px] border-b-2 -mb-px cursor-pointer select-none transition-colors duration-150 whitespace-nowrap", activeVersionId === v.id ? "text-teal-600 dark:text-teal-400 font-medium border-b-[3px] border-teal-500 dark:border-teal-400" : "text-gray-600 dark:text-gray-300 font-normal border-transparent hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800")
    }, v.label, /*#__PURE__*/React.createElement("span", {
      className: "text-[10px] text-gray-400 dark:text-gray-500"
    }, v.date)));
  })), activeVersion && /*#__PURE__*/React.createElement(ReportVersionPanel, {
    key: activeVersionId,
    version: activeVersion,
    onPrint: showActions ? printFn : undefined,
    onCopy: showActions ? onCopy : undefined
  }), /*#__PURE__*/React.createElement(ReportAITags, {
    tags: (_activeVersion$aiTags2 = activeVersion === null || activeVersion === void 0 ? void 0 : activeVersion.aiTags) !== null && _activeVersion$aiTags2 !== void 0 ? _activeVersion$aiTags2 : [],
    title: "AI Tags",
    onApplyTag: onApplyTag,
    applyingTagId: applyingTagId
  })))), showActions && /*#__PURE__*/React.createElement("div", {
    className: "h-0 overflow-hidden"
  }, /*#__PURE__*/React.createElement("div", {
    ref: printRef,
    className: "p-8 text-black flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "font-semibold text-base"
  }, data.examDescription), activeVersion && /*#__PURE__*/React.createElement("div", {
    className: "text-sm leading-relaxed whitespace-pre-wrap break-words",
    dangerouslySetInnerHTML: {
      __html: activeVersion.reportHtml.replace(/<[^>]+>/g, "")
    }
  })))));
};
export default RadiologyReportCard;